/**
 * What a device said back, on the terminal surface.
 *
 * One component for both places a shell command's output is read: the single
 * device's shell box on the device page, and a bulk run's grouped results.
 * They used to draw it twice and differently — the device page on white paper
 * with plain `monospace` and a hard-coded `#E5E7EB` border, bulk shell on the
 * dark surface with JetBrains Mono — so the same stdout looked like two
 * different kinds of thing depending on how you got to it.
 *
 * Dark in both themes on purpose: it reads as a shell, not as a card. That
 * makes it the only dark surface on these pages, which is the point — nothing
 * else is output.
 */

import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { colors, radius, SHELL_MONO, TERMINAL, TONES } from '@/theme/tokens';

export interface ShellOutputProps {
  stdout?: string;
  stderr?: string;
  /** Transport-level failure — the command never produced output. */
  error?: string;
  /** Sent, still out. Shown instead of the streams. */
  running?: boolean;
  /** Trailing note inside the block, e.g. "and 3 more devices". */
  footer?: ReactNode;
  maxHeight?: number;
}

export function ShellOutput({ stdout, stderr, error, running, footer, maxHeight = 360 }: ShellOutputProps) {
  const blocks: { label: string; value?: string; color?: string }[] = [
    { label: 'stdout', value: stdout },
    { label: 'stderr', value: stderr, color: TONES.error.fgDark },
    { label: 'error', value: error, color: TONES.error.fgDark },
  ];
  const any = blocks.some((b) => b.value);

  return (
    <Box
      sx={{
        bgcolor: TERMINAL.bg,
        borderRadius: `${radius.md}px`,
        px: 1.75,
        py: 1.5,
        fontFamily: SHELL_MONO,
        fontSize: 12,
        lineHeight: 1.6,
        color: colors.text.light,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        maxHeight,
        overflow: 'auto',
      }}
    >
      {running ? (
        <Box component="span" sx={{ color: TERMINAL.muted }}>Running…</Box>
      ) : (
        <>
          {!any && <Box component="span" sx={{ color: TERMINAL.label }}>(no output)</Box>}
          {blocks.map((b) =>
            b.value ? (
              <Box key={b.label} sx={{ mb: 1, color: b.color }}>
                <Box component="span" sx={{ color: TERMINAL.label }}>{b.label}</Box>
                {'\n'}
                {b.value.replace(/\n+$/, '')}
              </Box>
            ) : null,
          )}
        </>
      )}
      {footer && <Box sx={{ color: TERMINAL.label }}>{footer}</Box>}
    </Box>
  );
}

export default ShellOutput;
